/**
 * K4x.3c — the payout half of a deposit, on its own.
 *
 * WHY THIS IS SEPARATE FROM DepositEditModal
 * ------------------------------------------
 * K4x.3a deliberately left the payout block out: it is a different event.
 * Confirming a deposit happens at move-in; recording how it was paid back
 * happens once, at the end, and often weeks after the return itself was
 * flagged. Folding five more fields with their own four-way visibility rules
 * into the eight-field modal would rebuild the long form that modal exists to
 * escape.
 *
 * WHAT IT DOES NOT DO
 * -------------------
 * It does not own the return STATE. `deposit_returned` and
 * `deposit_returned_at` belong to K4x.3a's modal; this one reads
 * `deposit_returned` to decide whether a payout is meaningful and never writes
 * it. Saving a payout — or clearing one — leaves the status exactly as it was.
 *
 * It also derives no status of its own. `security_state` is the backend's
 * answer; nothing here recomputes it, before or after a save.
 *
 * TYPE-AGNOSTIC, ON PURPOSE
 * -------------------------
 * The backend couples the payout to nothing but `deposit_return_method` — not
 * to `tenant_deposit_type`. Whether an insurance guarantee should carry an
 * IBAN at all is a real domain question (K4x.3c discovery, remaining risk),
 * but it is not one this slice answers: inventing a client-side type gate here
 * would hide existing rows from the operator who has to repair them.
 *
 * THE PAYLOAD IS A FIXED LIST
 * ---------------------------
 * `buildPayoutPatchPayload` names its five fields one by one, and
 * `buildPayoutClearPayload` names the same five as explicit null. Both are
 * exported so a guard can assert them without rendering.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { ResponsiveModal } from "../../../../components/ui/responsive-modal";
import { patchAdminTenancy } from "../../../../api/adminData";
import { parseOptionalTenantDepositFloat } from "../tenantDetailHelpers";
import {
  DEPOSIT_RETURN_METHODS,
  depositMethodForcesZeroAmount,
  depositMethodUsesBankAccount,
  depositReturnMethodLabel,
} from "../../deposits/depositsLabels";

const FIELD =
  "w-full rounded-[8px] border border-border bg-background px-2.5 py-1.5 " +
  "text-[13px] text-foreground focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring disabled:opacity-50";
const LABEL = "mb-1 block text-[12px] font-medium text-muted-foreground";

/** The five payout columns, in wire order. One list, used by everything here. */
export const PAYOUT_FIELDS = [
  "deposit_return_method",
  "deposit_return_iban",
  "deposit_return_account_holder",
  "deposit_returned_amount",
  "deposit_return_note",
];

function emptyToNull(value) {
  const s = String(value || "").trim();
  return s === "" ? null : s;
}

/**
 * Whether this tenancy carries any stored payout detail.
 *
 * Exported and pure so the card's availability rule can be asserted without a
 * render. `deposit_returned_amount` is checked against null rather than
 * truthiness: a returned amount of 0 is a real, saved fact (`offset_only`
 * stores exactly that), and treating it as "nothing here" would hide the one
 * row an operator most needs to reach.
 */
export function hasDepositPayoutDetails(tenancy) {
  if (!tenancy) return false;
  return PAYOUT_FIELDS.some((key) => {
    const v = tenancy[key];
    if (v == null) return false;
    if (typeof v === "string") return v.trim() !== "";
    return true;
  });
}

/**
 * Whether the payout entry point should exist for this tenancy.
 *
 * Returned, OR carrying payout detail already. The second half is the repair
 * door: a row that says "not returned" while holding an IBAN is contradictory
 * stored data, and a rule of `deposit_returned === true` alone would lock the
 * operator out of the only screen that can fix it.
 */
export function canEditDepositPayout(tenancy) {
  if (!tenancy) return false;
  return !!tenancy.deposit_returned || hasDepositPayoutDetails(tenancy);
}

function formFromTenancy(tenancy) {
  return {
    method: String(tenancy?.deposit_return_method || ""),
    amount:
      tenancy?.deposit_returned_amount == null
        ? ""
        : String(tenancy.deposit_returned_amount),
    iban: String(tenancy?.deposit_return_iban || ""),
    holder: String(tenancy?.deposit_return_account_holder || ""),
    note: String(tenancy?.deposit_return_note || ""),
  };
}

/**
 * The five fields this modal may write.
 *
 * IBAN and holder are sent as explicit null for a method that does not use a
 * bank account, so the wire says what the screen shows — the backend clears
 * them anyway (`_clear_deposit_dates_if_not_flagged_patch`), this is the client
 * agreeing with it rather than enforcing a rule of its own. Same for the zero
 * amount under `offset_only`.
 *
 * The IBAN is stored as typed. The input renders uppercase in CSS only;
 * silently upper-casing the value would be this client inventing a
 * normalisation the backend does not perform.
 */
export function buildPayoutPatchPayload(form) {
  const method = emptyToNull(form.method);
  const usesBank = depositMethodUsesBankAccount(method);
  return {
    deposit_return_method: method,
    deposit_return_iban: usesBank ? emptyToNull(form.iban) : null,
    deposit_return_account_holder: usesBank ? emptyToNull(form.holder) : null,
    deposit_returned_amount: depositMethodForcesZeroAmount(method)
      ? 0
      : parseOptionalTenantDepositFloat(form.amount),
    deposit_return_note: emptyToNull(form.note),
  };
}

/**
 * Clearing the payout: all five keys, explicitly null.
 *
 * Not `{deposit_return_method: null}` alone. The backend's method-specific
 * clearing only fires for a method that IS set — sending a null method leaves
 * an existing IBAN, amount and note untouched in the database, so a "cleared"
 * payout would keep most of its data. PATCH is partial; a key that is not sent
 * is a key nobody touches. So all five go.
 */
export function buildPayoutClearPayload() {
  return {
    deposit_return_method: null,
    deposit_return_iban: null,
    deposit_return_account_holder: null,
    deposit_returned_amount: null,
    deposit_return_note: null,
  };
}

export default function DepositPayoutModal({
  tenancy,
  open,
  onOpenChange,
  onSaved,
}) {
  const [form, setForm] = useState(() => formFromTenancy(tenancy));
  const [saving, setSaving] = useState(false);

  // Re-seed on open and whenever the tenancy behind the card changes, so the
  // form never shows one tenancy's payout under another's name.
  useEffect(() => {
    if (open) setForm(formFromTenancy(tenancy));
  }, [open, tenancy]);

  const usesBank = depositMethodUsesBankAccount(form.method);
  const zeroAmount = depositMethodForcesZeroAmount(form.method);
  const hasStored = hasDepositPayoutDetails(tenancy);

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const submit = useCallback(
    async (payload, successMessage) => {
      if (saving || !tenancy?.id) return;
      setSaving(true);
      try {
        await patchAdminTenancy(tenancy.id, payload);
        // The parent refetches; nothing here guesses what the state became.
        if (typeof onSaved === "function") await onSaved();
        toast.success(successMessage);
        onOpenChange(false);
      } catch (err) {
        // The modal stays open with the entries intact: a rejected save is a
        // reason to correct something, not to retype everything.
        toast.error(err?.message || "Speichern fehlgeschlagen.");
      } finally {
        setSaving(false);
      }
    },
    [saving, tenancy, onSaved, onOpenChange]
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      submit(buildPayoutPatchPayload(form), "Rückzahlungsdetails gespeichert.");
    },
    [submit, form]
  );

  const handleClear = useCallback(() => {
    submit(buildPayoutClearPayload(), "Rückzahlungsdetails geleert.");
  }, [submit]);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
      title="Rückzahlungsdetails"
      description="Wie die Sicherheit abgerechnet wurde. Der Status selbst wird hier nicht verändert."
      contentClassName="max-w-[560px]"
    >
      <form
        onSubmit={handleSubmit}
        data-testid="deposit-payout-form"
        className="flex min-h-0 flex-1 flex-col text-foreground"
      >
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          {/* The one place the return state appears: as context, never as a
              control. Shown only when it contradicts the payout data, because
              that is the case an operator opened this screen to fix. */}
          {!tenancy?.deposit_returned && hasStored ? (
            <p
              data-testid="deposit-payout-contradiction"
              className="rounded-[8px] border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12px] text-foreground"
            >
              Diese Sicherheit ist nicht als zurückbezahlt oder freigegeben
              markiert, trägt aber Rückzahlungsdetails. Sie können die Angaben
              hier korrigieren oder leeren; der Status bleibt unverändert und
              wird in „Kaution bearbeiten“ gesetzt.
            </p>
          ) : null}

          <div>
            <label className={LABEL} htmlFor="deposit-payout-method">
              Rückzahlungsart
            </label>
            <select
              id="deposit-payout-method"
              data-testid="deposit-payout-method"
              className={FIELD}
              value={form.method}
              onChange={set("method")}
              disabled={saving}
            >
              <option value="">—</option>
              {DEPOSIT_RETURN_METHODS.map((m) => (
                <option key={m} value={m}>
                  {depositReturnMethodLabel(m)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={LABEL} htmlFor="deposit-payout-amount">
              Zurückbezahlt (CHF)
            </label>
            <input
              id="deposit-payout-amount"
              data-testid="deposit-payout-amount"
              type="number"
              min="0"
              step="0.01"
              className={FIELD}
              /* Vollständig verrechnet means nothing was paid out; the backend
                 stores 0 for it either way, so the field shows that instead of
                 accepting a number it would discard. */
              value={zeroAmount ? "0" : form.amount}
              onChange={set("amount")}
              disabled={saving || zeroAmount}
            />
          </div>

          {usesBank ? (
            <>
              <div>
                <label className={LABEL} htmlFor="deposit-payout-iban">
                  IBAN
                </label>
                <input
                  id="deposit-payout-iban"
                  data-testid="deposit-payout-iban"
                  type="text"
                  maxLength={34}
                  className={`${FIELD} uppercase`}
                  value={form.iban}
                  onChange={set("iban")}
                  disabled={saving}
                />
              </div>
              <div>
                <label className={LABEL} htmlFor="deposit-payout-holder">
                  Kontoinhaber
                </label>
                <input
                  id="deposit-payout-holder"
                  data-testid="deposit-payout-holder"
                  type="text"
                  className={FIELD}
                  value={form.holder}
                  onChange={set("holder")}
                  disabled={saving}
                />
              </div>
            </>
          ) : null}

          <div>
            <label className={LABEL} htmlFor="deposit-payout-note">
              Notiz
            </label>
            <textarea
              id="deposit-payout-note"
              data-testid="deposit-payout-note"
              rows={3}
              className={FIELD}
              value={form.note}
              onChange={set("note")}
              disabled={saving}
            />
          </div>
        </div>

        <div className="mt-3 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
          {/* Only offered when there is something stored to remove — a clear
              action on an empty payout would send five nulls to no effect. */}
          {hasStored ? (
            <button
              type="button"
              data-testid="deposit-payout-clear"
              disabled={saving}
              onClick={handleClear}
              className="mr-auto rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[13px] font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              Rückzahlungsdetails leeren
            </button>
          ) : null}
          <button
            type="button"
            disabled={saving}
            onClick={() => onOpenChange(false)}
            className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            data-testid="deposit-payout-save"
            disabled={saving}
            className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3 py-1.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Speichert …" : "Speichern"}
          </button>
        </div>
      </form>
    </ResponsiveModal>
  );
}
